<template>
  <div class="admin-page">
    <!-- Page Header -->
    <div class="page-header">
      <div class="header-content">
        <div class="header-icon">
          <i class="fas fa-cog"></i>
        </div>
        <div class="header-text">
          <h2>Administration</h2>
          <p>Gérez les paramètres et configurations de la plateforme</p>
        </div>
      </div>
    </div>

    <!-- Navigation Tabs -->
    <div class="admin-nav">
      <button 
        v-for="tab in tabs" 
        :key="tab.id"
        @click="activeTab = tab.id"
        class="nav-tab"
        :class="{ active: activeTab === tab.id }"
      >
        <i :class="tab.icon"></i>
        <span>{{ tab.label }}</span>
        <span v-if="tab.badge" class="tab-badge">{{ tab.badge }}</span>
      </button>
    </div>

    <!-- Tab Content -->
    <div class="tab-content">
      <!-- Onglet Utilisateurs -->
      <div v-if="activeTab === 'users'" class="tab-pane">
        <UsersManager />
      </div>

      <!-- Onglet Catégories -->
      <div v-if="activeTab === 'categories'" class="tab-pane">
        <CategoriesManager />
      </div>

      <!-- Onglet Paramètres (à venir) -->
      <div v-if="activeTab === 'settings'" class="tab-pane">
        <div class="coming-soon">
          <i class="fas fa-cog"></i>
          <h3>Paramètres Généraux</h3>
          <p>Cette section sera bientôt disponible</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import UsersManager from '@/components/admin/UsersManager.vue';
import CategoriesManager from '@/components/admin/CategoriesManager.vue';

const activeTab = ref('users');

const tabs = computed(() => [
  {
    id: 'users',
    label: 'Utilisateurs',
    icon: 'fas fa-users',
    badge: null
  },
  {
    id: 'categories',
    label: 'Catégories',
    icon: 'fas fa-layer-group',
    badge: null
  },
  {
    id: 'settings',
    label: 'Paramètres',
    icon: 'fas fa-cog',
    badge: null
  }
]);
</script>

<style scoped>
.admin-page {
  padding: 2rem;
  max-width: 1600px;
  margin: 0 auto;
}

/* Page Header */
.page-header {
  background: linear-gradient(135deg, rgba(255,255,255,0.95), rgba(255,255,255,0.85));
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 4px 20px rgba(0,27,183,0.08);
}

.header-content {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.header-icon {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.75rem;
  box-shadow: 0 4px 15px rgba(0,27,183,0.3);
}

.header-text h2 {
  font-size: 1.75rem;
  font-weight: 800;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 0.25rem;
}

.header-text p {
  color: #6c757d;
  font-size: 0.95rem;
}

/* Navigation Tabs */
.admin-nav {
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  background: rgba(255,255,255,0.7);
  backdrop-filter: blur(10px);
  padding: 1rem;
  border-radius: 16px;
  box-shadow: 0 2px 10px rgba(0,27,183,0.05);
}

.nav-tab {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1.5rem;
  background: transparent;
  border: 2px solid transparent;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.9375rem;
  color: #64748b;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
}

.nav-tab i {
  font-size: 1.125rem;
}

.nav-tab:hover {
  background: rgba(0,27,183,0.05);
  color: var(--primary-blue);
  transform: translateY(-2px);
}

.nav-tab.active {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
  box-shadow: 0 4px 15px rgba(0,27,183,0.3);
}

.tab-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 0.5rem;
  background: rgba(255,255,255,0.2);
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 700;
}

.nav-tab.active .tab-badge {
  background: rgba(255,255,255,0.3);
}

/* Tab Content */
.tab-content {
  animation: fadeIn 0.3s ease;
}

.tab-pane {
  animation: fadeInUp 0.4s ease;
}

/* Coming Soon */
.coming-soon {
  text-align: center;
  padding: 6rem 2rem;
  background: white;
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(0,27,183,0.08);
}

.coming-soon i {
  font-size: 4rem;
  color: var(--primary-blue);
  margin-bottom: 1.5rem;
  opacity: 0.5;
}

.coming-soon h3 {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.75rem;
}

.coming-soon p {
  color: #64748b;
  font-size: 1.0625rem;
}

/* Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .admin-page {
    padding: 1rem;
  }

  .admin-nav {
    flex-direction: column;
    gap: 0.5rem;
  }

  .nav-tab {
    width: 100%;
    justify-content: flex-start;
  }

  .header-content {
    flex-direction: column;
    text-align: center;
  }
}
</style>
